// src/hooks/useAIAnalysis.ts - AI analysis logic for BCP form
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { FormState } from '../types/bcpForm';
import { analyzeFormData, createBCP } from '../services/api';
import {
  getAnalysisStrategy,
  shouldTriggerAI
} from '../utils/aiAnalysisUtils';

export interface AIInsight {
  type: 'risk' | 'suggestion' | 'warning' | 'info';
  message: string;
  priority: 'high' | 'medium' | 'low';
  category: string;
}

export interface StepAnalysis {
  insights: AIInsight[];
  completion_score: number;
}

export interface AIAnalysis {
  step_insights: {
    [step: string]: StepAnalysis;
  };
}

// Legacy format for backward compatibility
export interface LegacyAIAnalysis {
  insights: AIInsight[];
  completion_score: number;
  next_steps: string[];
}

export interface AIAnalysisHook {
  analysis: AIAnalysis | null;
  isAnalyzing: boolean;
  lastAnalysis: string;
  currentStepAnalysis: StepAnalysis | null;
  shouldShowAnalysis: (step: number, formData: FormState, risks?: any, employees?: any[]) => boolean;
}

export const useAIAnalysis = (
  formData: FormState,
  currentStep: number,
  isVisible: boolean,
  risks?: { primary_concerns: string[]; location_risks: string[] },
  employees?: any[],
  bcpId?: string,
  isEditing?: boolean
): AIAnalysisHook => {
  const [analysis, setAnalysis] = useState<AIAnalysis | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [lastAnalysis, setLastAnalysis] = useState<string>('');
  const [autoSavedBcpId, setAutoSavedBcpId] = useState<string | null>(null);
  const lastStepRef = useRef<number>(currentStep);

  // Auto-save function for new plans to ensure BCP ID availability
  const ensureBCPExists = useCallback(async (): Promise<string> => {
    // If we already have a BCP ID (existing plan or previously auto-saved), use it
    if (bcpId) return bcpId;
    if (autoSavedBcpId) return autoSavedBcpId;

    // For new plans, auto-save to get BCP ID
    // Backend handles duplicate business names by updating existing BCP
    try {
      console.log('🔄 [AI ANALYSIS] Auto-saving new BCP to get ID for AI analysis...');
      const savedBCP = await createBCP(formData);
      const newBcpId = savedBCP.id.toString();
      setAutoSavedBcpId(newBcpId);
      console.log('✅ [AI ANALYSIS] BCP auto-saved with ID:', newBcpId);
      return newBcpId;
    } catch (error) {
      console.error('❌ [AI ANALYSIS] Auto-save failed:', error);
      throw error;
    }
  }, [bcpId, autoSavedBcpId, formData]);

  // Check if AI should trigger based on form data completeness
  const shouldTrigger = useMemo(() => {
    // For existing plans, only trigger AI when actively editing
    if (bcpId && !isEditing) return false;

    // For new plans or when editing existing plans, check if we have complete basic data
    return !!(
      formData.business_name &&
      formData.business_name.length > 2 &&
      formData.business_type &&
      formData.industry &&
      formData.employee_count > 0 &&
      formData.primary_location.name &&
      formData.primary_location.address &&
      formData.primary_location.state &&
      formData.primary_location.zip
    );
  }, [
    formData.business_name,
    formData.business_type,
    formData.industry,
    formData.employee_count,
    formData.primary_location.name,
    formData.primary_location.address,
    formData.primary_location.state,
    formData.primary_location.zip,
    bcpId,
    isEditing
  ]);

  // Memoize the form hash for caching - only changes when actual data changes, not step navigation
  const formHash = useMemo(() => {
    if (!shouldTrigger) return '';

    return JSON.stringify({
      business_name: formData.business_name,
      business_type: formData.business_type,
      industry: formData.industry,
      employee_count: formData.employee_count,
      critical_functions: formData.critical_functions.length,
      it_systems: formData.it_systems.length,
      key_suppliers: formData.key_suppliers.length,
      primary_concerns: risks?.primary_concerns?.length || 0,
      location_risks: risks?.location_risks?.length || 0,
      employees_count: employees?.length || 0,
      bcpId: bcpId || 'new'
    });
  }, [
    shouldTrigger,
    formData.business_name,
    formData.business_type,
    formData.industry,
    formData.employee_count,
    formData.critical_functions.length,
    formData.it_systems.length,
    formData.key_suppliers.length,
    risks?.primary_concerns?.length,
    risks?.location_risks?.length,
    employees?.length,
    bcpId
  ]);

  // Generate local fallback insights for a specific step
  const generateLocalStepInsights = useCallback((data: FormState, step: number): StepAnalysis => {
    const insights: AIInsight[] = [];
    let score = 0;

    // Step 1: Business Information
    if (step === 1) {
      if (data.business_name) score += 20;
      if (data.business_type) score += 15;
      if (data.industry) score += 15;
      if (data.employee_count) score += 15;
      if (data.address || data.primary_location.address) score += 15;
      if (data.primary_location.name) score += 10;
      if (data.primary_location.contact) score += 5;
      if (data.primary_location.phone) score += 5;

      if (data.business_type && data.industry) {
        if (data.business_type === 'manufacturing') {
          insights.push({
            type: 'suggestion',
            message: 'Manufacturing businesses should prioritize production line continuity and supply chain resilience',
            priority: 'high',
            category: 'Business Type'
          });
        } else if (data.business_type === 'healthcare') {
          insights.push({
            type: 'warning',
            message: 'Healthcare businesses must consider HIPAA compliance and patient care continuity',
            priority: 'high',
            category: 'Compliance'
          });
        }
      }

      if (!data.business_name) {
        insights.push({
          type: 'warning',
          message: 'Business name is required to create your BCP',
          priority: 'high',
          category: 'Required Field'
        });
      }
    }

    // Step 2: Risk Assessment
    if (step === 2) {
      const primaryCount = risks?.primary_concerns?.length || 0;
      const locationCount = risks?.location_risks?.length || 0;

      if (primaryCount > 0) score += 50;
      if (locationCount > 0) score += 50;

      if (primaryCount > 0) {
        insights.push({
          type: 'info',
          message: `${primaryCount} primary concerns identified - ensure each has mitigation strategies`,
          priority: 'medium',
          category: 'Risk Coverage'
        });
      } else {
        insights.push({
          type: 'warning',
          message: 'No primary concerns identified - this is critical for BCP effectiveness',
          priority: 'high',
          category: 'Risk Assessment'
        });
      }
    }

    // Step 3: Business Impact Analysis
    if (step === 3) {
      const functionsCount = data.critical_functions?.length || 0;
      if (functionsCount > 0) score += 100;

      if (functionsCount > 0) {
        insights.push({
          type: 'info',
          message: `${functionsCount} critical functions identified - ensure RTOs and RPOs are realistic`,
          priority: 'medium',
          category: 'Business Impact'
        });
      } else {
        insights.push({
          type: 'warning',
          message: 'No critical functions identified - this is essential for business impact analysis',
          priority: 'high',
          category: 'Business Impact'
        });
      }
    }

    // Step 4: Critical Operations
    if (step === 4) {
      const itCount = data.it_systems?.length || 0;
      const suppliersCount = data.key_suppliers?.length || 0;

      if (itCount > 0) score += 50;
      if (suppliersCount > 0) score += 50;

      if (itCount === 0) {
        insights.push({
          type: 'warning',
          message: 'No IT systems documented - even basic systems like email should be included',
          priority: 'high',
          category: 'IT Infrastructure'
        });
      }
      if (suppliersCount === 0) {
        insights.push({
          type: 'warning',
          message: 'No suppliers documented - identify vendors critical to operations',
          priority: 'high',
          category: 'Supply Chain'
        });
      }
    }

    // Step 5: Mitigations
    if (step === 5) {
      // Count mitigations from risks
      const mitigationCount = 0; // This would be calculated from actual mitigation data

      if (mitigationCount > 0) score += 100;

      insights.push({
        type: 'suggestion',
        message: 'Create specific mitigation strategies for each identified risk',
        priority: 'high',
        category: 'Mitigation Planning'
      });
    }

    return {
      insights,
      completion_score: Math.min(score, 100)
    };
  }, [risks]);

  // Generate real-time insights based on hybrid strategy
  useEffect(() => {
    if (!isVisible || !shouldTrigger || !formHash) return;

    // Check if only step changed without data changes
    const isJustStepChange = lastStepRef.current !== currentStep && formHash === lastAnalysis;

    if (isJustStepChange) {
      console.log(`🔄 [AI ANALYSIS] Step changed from ${lastStepRef.current} to ${currentStep} - using cached analysis`);
      lastStepRef.current = currentStep;
      return;
    }

    // If data actually changed, proceed with analysis
    if (formHash === lastAnalysis) return;

    console.log(`📊 [AI ANALYSIS] Data changed - triggering new analysis`);
    setIsAnalyzing(true);

    // Use fixed debounce timing to prevent excessive calls
    const timer = setTimeout(async () => {
      try {
        console.log(`🤖 [AI ANALYSIS] Starting analysis for step ${currentStep}`);

        // Ensure BCP exists for richer analysis (auto-save if needed)
        const actualBcpId = await ensureBCPExists();
        console.log(`📋 [AI ANALYSIS] Using BCP ID: ${actualBcpId}`);

        // Use form data analysis (works for both new and existing plans)
        const result = await analyzeFormData(formData);

        // Check if result has the new structure
        if (result && result.step_insights) {
          setAnalysis(result as AIAnalysis);
        } else {
          // Legacy format - convert to new structure or use fallback
          console.log('⚠️ [AI ANALYSIS] Received legacy format, generating local fallback');
          const allStepInsights: { [step: string]: StepAnalysis } = {};
          for (let i = 1; i <= 5; i++) {
            allStepInsights[i.toString()] = generateLocalStepInsights(formData, i);
          }
          setAnalysis({ step_insights: allStepInsights });
        }

        setLastAnalysis(formHash);
        lastStepRef.current = currentStep;

        console.log(`✅ [AI ANALYSIS] Analysis completed`);
      } catch (error) {
        console.error('❌ [AI ANALYSIS] Analysis error:', error);

        // Fallback to local analysis for all steps
        const allStepInsights: { [step: string]: StepAnalysis } = {};
        for (let i = 1; i <= 5; i++) {
          allStepInsights[i.toString()] = generateLocalStepInsights(formData, i);
        }
        setAnalysis({ step_insights: allStepInsights });
        setLastAnalysis(formHash);
        lastStepRef.current = currentStep;
      } finally {
        setIsAnalyzing(false);
      }
    }, 1500); // Fixed 1.5 second debounce

    return () => clearTimeout(timer);
  }, [
    formHash,
    isVisible,
    lastAnalysis,
    shouldTrigger,
    currentStep,
    ensureBCPExists,
    generateLocalStepInsights,
    formData
  ]);

  // Get current step analysis
  const currentStepAnalysis = useMemo(() => {
    if (!analysis || !analysis.step_insights) return null;
    return analysis.step_insights[currentStep.toString()] || null;
  }, [analysis, currentStep]);

  const shouldShowAnalysis = useCallback((step: number, formData: FormState, risks?: any, employees?: any[]) => {
    // Use the hybrid strategy to determine if analysis should be shown
    const currentStrategy = getAnalysisStrategy(step, formData, bcpId, risks, employees);
    return shouldTriggerAI(currentStrategy, formData, risks, employees);
  }, [bcpId]);

  return {
    analysis,
    isAnalyzing,
    lastAnalysis,
    currentStepAnalysis,
    shouldShowAnalysis,
  };
};
